import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { EvidenceCard, metricRows } from '@/pages/anatel/conselho/shared';
import { ContestedItemsTable } from '@/pages/anatel/conselho/ContestedItemsTable';
import { IntervalList } from '@/pages/anatel/conselho/IntervalList';
import { RunFooter } from '@/pages/anatel/conselho/RunFooter';
import { MinutesTable } from '@/pages/cade/conselho/MinutesTable';
import { cadeScopeLabel } from '@/pages/cade/conselho/scope';
import { MemberOutvotedTable, RelatorOutvotedTable } from '@/pages/cade/conselho/VistaDocketPanels';
import { conselhoCasesHref } from '@/pages/cade/ConselhoCasesPage';
import type { PersonConselho } from '@/services/types';

/** Model-derived block on one conselheiro's page. Every Answer uses scope="record". */
export function CadePersonConselhoPanels({ conselho }: { conselho: PersonConselho }) {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const signed = new Intl.NumberFormat(i18n.language, { signDisplay: 'exceptZero', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 });
  const residual = (v: number) => signed.format(v);
  const scopeLabel = (rowName: string) =>
    cadeScopeLabel(rowName.includes(' — ') ? rowName.split(' — ')[1] : 'overall', t);

  return (
    <section className="space-y-4 rounded-lg border bg-card p-4" data-panel="conselho">
      <h2 className="text-sm font-semibold text-foreground">{t('cade:conselheiro.conselho.title')}</h2>
      <Link
        to="/cade/conselho/methodology"
        className="inline-block text-xs text-primary underline-offset-4 hover:underline"
        data-testid="methodology-link"
      >
        {t('common:conselhoMethodology.link')}
      </Link>
      <EvidenceCard evidence={conselho.evidence} ns="cade" />

      <div data-panel="conselho-severity">
        <h3 className="mb-2 text-xs font-semibold text-foreground">{t('cade:conselheiro.conselho.severityTitle')}</h3>
        <Answer answer={conselho.severity} help={t('cade:conselheiro.conselho.severityHelp')} scope="record">
          {(rows) => (
            <IntervalList
              rows={metricRows(rows).map((r) => ({ ...r, name: scopeLabel(r.name) }))}
              axisLabel={t('cade:conselho.severity.axis')}
              format={residual}
              rawFormat={(v) => percent.format(v)}
              rawLabel={t('cade:conselho.severity.raw')}
            />
          )}
        </Answer>
        <RunFooter run={conselho.severity_run} ns="cade" />
      </div>

      <div data-panel="conselho-minutes">
        <h3 className="mb-2 text-xs font-semibold text-foreground">{t('cade:conselheiro.conselho.minutesTitle')}</h3>
        {conselho.minutes_record ? (
          <Answer answer={conselho.minutes_record} help={t('cade:conselho.minutes.help')} scope="record">
            {(rows) => <MinutesTable rows={rows} linked={false} />}
          </Answer>
        ) : null}
        {/* A member with opportunities gets their share; no complete year
            at all is the track's refusal (corpus scope); complete years without
            this member is a thin record. */}
        <div className="mt-2" data-panel="conselho-follow">
          <h4 className="mb-1 text-xs font-semibold text-foreground">{t('cade:conselho.minutes.followTitle')}</h4>
          <Answer
            answer={conselho.follow}
            help={t('cade:conselho.minutes.followHelp')}
            scope={conselho.follow.substrate === 'insufficient_sample' ? 'record' : 'corpus'}
          >
            {(rows) => (
              <>
                <IntervalList
                  rows={metricRows(rows).map((r, i) => ({ ...r, name: rows[i].scope }))}
                  axisLabel={t('cade:conselho.minutes.followAxis')}
                  format={(v) => percent.format(v)}
                  zeroLine={false}
                />
                <Link
                  to={conselhoCasesHref('follow', rows[0].name)}
                  className="mt-1 inline-block text-xs text-primary underline-offset-4 hover:underline"
                  data-testid="follow-items-link"
                >
                  {t('cade:conselheiro.conselho.seeItems')}
                </Link>
              </>
            )}
          </Answer>
        </div>
        {conselho.outvoted ? (
          <div className="mt-3" data-panel="conselho-outvoted">
            <h4 className="mb-1 text-xs font-semibold text-foreground">{t('cade:conselho.outvoted.title')}</h4>
            <Answer answer={conselho.outvoted} help={t('cade:conselho.outvoted.help')} scope="record">
              {(rows) => <MemberOutvotedTable rows={rows} linked={false} />}
            </Answer>
          </div>
        ) : null}
        {conselho.pairs ? (
          <div className="mt-3" data-panel="conselho-pairs">
            <h4 className="mb-1 text-xs font-semibold text-foreground">{t('cade:conselheiro.conselho.pairsTitle')}</h4>
            <Answer answer={conselho.pairs} help={t('cade:conselho.pairs.help')} scope="record">
              {(rows) => (
                <ul className="space-y-1 text-xs" data-testid="conselho-pairs-list">
                  {rows.map((r) => (
                    <li
                      key={`${r.slug_a}|${r.slug_b}`}
                      className={`flex flex-wrap justify-between gap-2 ${r.suppressed ? 'text-muted-foreground' : ''}`}
                      data-suppressed={r.suppressed ? 'true' : 'false'}
                    >
                      <Link to={conselhoCasesHref('pair', r.name_a, r.name_b)} className="underline-offset-4 hover:underline">
                        {r.name_a} — {r.name_b}
                      </Link>
                      <span className="tabular-nums">
                        {t('cade:conselheiro.conselho.pairLine', {
                          contested: r.n_contested_co_sat,
                          share:
                            r.agreement_rate_contested == null
                              ? '—'
                              : `${percent.format(r.agreement_rate_contested)} [${percent.format(r.lower_contested ?? 0)}, ${percent.format(r.upper_contested ?? 0)}]`,
                        })}
                        {r.suppressed ? ` (${t('cade:conselho.pairs.belowFloor')})` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Answer>
          </div>
        ) : null}
        {conselho.relator_outvoted ? (
          <div className="mt-3" data-panel="conselho-relator-outvoted">
            <h4 className="mb-1 text-xs font-semibold text-foreground">{t('cade:conselho.relatorOutvoted.title')}</h4>
            <Answer answer={conselho.relator_outvoted} help={t('cade:conselho.relatorOutvoted.help')} scope="record">
              {(rows) => <RelatorOutvotedTable rows={rows} linked={false} />}
            </Answer>
          </div>
        ) : null}
      </div>

      <div data-panel="conselho-positions">
        <h3 className="mb-2 text-xs font-semibold text-foreground">{t('cade:conselheiro.conselho.positionsTitle')}</h3>
        <Answer answer={conselho.positions} help={t('cade:conselheiro.conselho.positionsHelp')} scope="record">
          {(rows) => (
            <IntervalList
              rows={rows.map((r) => ({
                key: r.period,
                name: r.period === 'all' ? t('cade:conselheiro.conselho.overall') : r.period,
                n: r.n_items,
                nLabel: t('cade:conselho.positions.contested'),
                effect: r.position,
                lower: r.lower,
                upper: r.upper,
                suppressed: r.suppressed,
              }))}
              axisLabel={t('cade:conselho.positions.axis')}
              format={residual}
            />
          )}
        </Answer>
        <RunFooter run={conselho.position_run} ns="cade" />
      </div>

      {conselho.contested_items.length > 0 ? (
        <div data-panel="conselho-items">
          <h3 className="mb-1 text-xs font-semibold text-foreground">{t('cade:conselheiro.conselho.itemsTitle')}</h3>
          <p className="mb-2 text-[11px] text-muted-foreground">{t('cade:conselheiro.conselho.itemsHelp')}</p>
          <ContestedItemsTable
            items={conselho.contested_items}
            showSide
            ns="cade"
            processBase="/cade/c"
            relatorBase="/cade/r"
          />
        </div>
      ) : null}
    </section>
  );
}
